/**
 * Your own channels (profile and portal): WhatsApp and email, each with its state, a
 * switch to mute it, and the one step that connects it. Telegram keeps its own card.
 * Also exports the dashboard nudge shown to staff until WhatsApp is connected (ADR-W4).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, MessageCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import {
  fetchMyChannels, myEmailConfirm, myWhatsAppLink, myWhatsAppUnlink, setMyChannelPrefs, errText,
  type ChannelState,
} from '@/lib/connections';
import WhatsAppLinkDialog from './WhatsAppLinkDialog';

export function Pill({ s }: { s: ChannelState }) {
  const tone = s.connected ? (s.on ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-slate-700/40 text-slate-300 border-slate-600')
    : s.waiting ? 'bg-amber-500/10 text-amber-300 border-amber-500/30' : 'bg-slate-800 text-muted border-slate-700';
  const label = s.connected ? (s.on ? 'Connected' : 'Muted') : s.waiting ? 'Waiting' : 'Not connected';
  return <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px]', tone)}>{label}</span>;
}

function Toggle({ on, onChange, disabled, label }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)}
      className={cn('relative w-10 h-6 rounded-full transition disabled:opacity-40', on ? 'bg-emerald-500/70' : 'bg-slate-700')}>
      <span className={cn('absolute top-0.5 w-5 h-5 rounded-full bg-white transition', on ? 'left-[18px]' : 'left-0.5')} />
    </button>
  );
}

export default function MyChannelsCard({ name }: { name: string }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['my-channels'], queryFn: fetchMyChannels });
  const [linking, setLinking] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['my-channels'] });
  const prefs = useMutation({ mutationFn: setMyChannelPrefs, onSuccess: refresh, onError: (e) => toast.error(errText(e, 'Could not save')) });
  const unlink = useMutation({ mutationFn: myWhatsAppUnlink, onSuccess: () => { toast.success('WhatsApp disconnected'); refresh(); } });
  const confirm = useMutation({ mutationFn: myEmailConfirm, onSuccess: (r) => (r.ok ? toast.success(r.message) : toast.error(r.message)) });
  const d = me.data;

  return (
    <Card className="mb-4" data-testid="my-channels">
      <h3 className="font-medium text-muted-strong mb-1">Where you get notified</h3>
      <p className="text-xs text-muted mb-4">Reviews, mentions, the set feed and the daily digest reach you on every channel you connect and leave on.</p>
      {!d && <div className="text-sm text-muted">Loading...</div>}
      {d && (
        <div className="divide-y divide-slate-800/70">
          <div className="flex flex-wrap items-center gap-3 py-3" data-testid="my-whatsapp">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 text-emerald-300 flex items-center justify-center shrink-0"><MessageCircle size={18} /></div>
            <div className="flex-1 min-w-[180px]">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-strong">WhatsApp</span><Pill s={d.whatsapp} />
                {d.whatsapp_required && !d.whatsapp.connected && <span className="text-[11px] text-amber-300">required for the team</span>}
              </div>
              <div className="text-xs text-muted">{d.whatsapp.connected ? d.whatsapp.hint : 'Messages from the KAF PRO number, to your own phone.'}</div>
            </div>
            {d.whatsapp.connected ? (
              <div className="flex items-center gap-3">
                <Toggle on={d.whatsapp.on} label="WhatsApp notifications" disabled={prefs.isPending} onChange={(v) => prefs.mutate({ whatsapp: v })} />
                <Button size="sm" variant="ghost" onClick={() => unlink.mutate()} disabled={unlink.isPending}>Disconnect</Button>
              </div>
            ) : (
              <Button size="sm" onClick={() => setLinking(true)} data-testid="my-whatsapp-connect">Connect WhatsApp</Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 py-3" data-testid="my-email">
            <div className="w-9 h-9 rounded-lg bg-accent-600/20 text-accent-400 flex items-center justify-center shrink-0"><Mail size={18} /></div>
            <div className="flex-1 min-w-[180px]">
              <div className="flex items-center gap-2"><span className="text-sm text-muted-strong">Email</span><Pill s={d.email} /></div>
              <div className="text-xs text-muted">{d.email.hint}</div>
            </div>
            {d.email.connected ? (
              <Toggle on={d.email.on} label="Email notifications" disabled={prefs.isPending} onChange={(v) => prefs.mutate({ email: v })} />
            ) : (
              <Button size="sm" variant="secondary" onClick={() => confirm.mutate()} disabled={confirm.isPending}>Send me the confirm link</Button>
            )}
          </div>
        </div>
      )}
      <WhatsAppLinkDialog open={linking} self who={name} onClose={() => { setLinking(false); refresh(); }}
        getLink={myWhatsAppLink} isConnected={async () => (await fetchMyChannels()).whatsapp.connected} />
    </Card>
  );
}

/** Dashboard: staff without WhatsApp see this until they connect (ADR-W4). */
export function ConnectWhatsAppNudge({ name }: { name: string }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['my-channels'], queryFn: fetchMyChannels });
  const [linking, setLinking] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem('wa-nudge-hidden') === '1'; } catch { return false; }
  });
  const d = me.data;
  if (!d || !d.whatsapp_required || d.whatsapp.connected || !d.whatsapp_ready || hidden) return null;
  return (
    <div className="mb-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="wa-nudge">
      <div className="w-9 h-9 rounded-lg bg-emerald-500/15 text-emerald-300 flex items-center justify-center shrink-0"><MessageCircle size={18} /></div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm text-muted-strong">Connect your WhatsApp</div>
        <div className="text-xs text-muted">The team gets reviews, mentions and the set feed on WhatsApp. One tap on your phone.</div>
      </div>
      <Button size="sm" onClick={() => setLinking(true)} data-testid="wa-nudge-connect">Connect now</Button>
      <button className="text-muted hover:text-muted-strong" aria-label="Later"
        onClick={() => { setHidden(true); try { sessionStorage.setItem('wa-nudge-hidden', '1'); } catch { /* private window */ } }}><X size={16} /></button>
      <WhatsAppLinkDialog open={linking} self who={name} onClose={() => { setLinking(false); qc.invalidateQueries({ queryKey: ['my-channels'] }); }}
        getLink={myWhatsAppLink} isConnected={async () => (await fetchMyChannels()).whatsapp.connected} />
    </div>
  );
}
